/* eslint-disable react/prop-types */
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { observer } from 'mobx-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Form, FormControlProps } from 'react-bootstrap';
import { ChangeHandler, InternalFieldName } from 'react-hook-form';

interface FileListProps {
  files: FileList;
}

const FileList = observer(({ files }: FileListProps) => {
  const rows: File[] = [];
  for (let i = 0; i < files.length; ++i) rows.push(files[i]);
  return (
    <ul className="list-group w-100">
      {rows.map((file, index) => (
        <li key={index + '_' + uniqueId()} className="list-group-item">
          <input
            type="text"
            className="form-control flex-grow-1"
            readOnly={true}
            value={file.name}
          />
        </li>
      ))}
    </ul>
  );
});

export interface FileUploadProps extends FormControlProps {
  labelText?: string;
  emptyText?: string;
  uploadButtonText: string;
  isInvalid?: boolean;
  busy?: boolean;
  shouldClearFiles?: boolean;
  accept?: string;
  multiple?: boolean;
  name: InternalFieldName;
  className?: string;
  helpText?: string;
  filesAcquiredCallback?: (files: FileList) => void; //need this to pass selected files out so we can render a preview of the file being uploaded
  onChange: ChangeHandler;
  onBlur: ChangeHandler;
}
// If defined, both are required
type IDirtyTrack =
  | {
      highlightDirty?: undefined;
      isDirty?: undefined;
    }
  | {
      highlightDirty: boolean;
      isDirty: boolean | undefined;
    };

const FileUploadWithForwardedRef = React.forwardRef(
  (
    {
      uploadButtonText,
      emptyText,
      labelText,
      accept,
      multiple,
      isInvalid,
      shouldClearFiles: shouldClearFiles,
      busy,
      isDirty,
      highlightDirty,
      filesAcquiredCallback,
      ...props
    }: FileUploadProps & IDirtyTrack,
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [fileList, setFileList] = useState<FileList | null>(null);
    const fileCount = fileList !== null ? fileList.length : 0;

    const controlId = useMemo(
      () =>
        `form-${(labelText ? labelText : uniqueId()).replaceAll(/[\W_]/gi, '').toLowerCase()}`,
      [labelText],
    );

    if (!props.name)
      throw 'register was not called with a valid name for BlobFileUpload';
    useEffect(() => {
      if (!inputRef) return;

      if (typeof ref === 'function') {
        ref(inputRef.current);
        if (inputRef && inputRef.current) setFileList(inputRef.current.files);
      } else {
        if (ref !== null) ref.current = inputRef.current;
      }
    }, [inputRef, ref, props.name]);

    const uploadButtonClicked = useCallback(
      (e: any) => {
        if (inputRef && inputRef.current) {
          inputRef.current.click();
          e.stopPropagation();
        }
      },
      [inputRef],
    );

    const onChangeImpl = useCallback(
      async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files) {
          const fileList = event.target.files;
          if (filesAcquiredCallback) {
            filesAcquiredCallback(fileList);
          }

          setFileList(fileList);
        }
        return await props.onChange(event).then(() => {
          if (shouldClearFiles) {
            setFileList(null);
          }
        }); //pass up the onChange to react hook forms register onChange
      },
      [setFileList, props, filesAcquiredCallback, shouldClearFiles],
    );

    return (
      <div>
        <Form.Control
          data-testid={controlId + '-hidden'}
          className="visually-hidden cursor-pointer"
          type="file"
          accept={accept ? accept.toString() : undefined}
          ref={inputRef}
          multiple={multiple}
          isInvalid={isInvalid}
          name={props.name}
          onChange={onChangeImpl}
          onBlur={props.onBlur}
        />
        {labelText && (
          <Form.Label className="form-label">{labelText}</Form.Label>
        )}
        <div className="form-control-group">
          {fileList && fileCount > 1 && (
            <>
              <FileList files={fileList} />
              <div className="d-flex flex-row justify-content-end mt-2">
                <Button
                  variant="primary"
                  style={{ zIndex: 0 }}
                  type="button"
                  onClick={uploadButtonClicked}
                >
                  Reselect File{multiple ? 's' : ''}
                </Button>
              </div>
            </>
          )}
          {fileCount <= 1 && (
            <div
              className="input-group cursor-pointer"
              onClick={uploadButtonClicked}
            >
              <label
                className={classNames('form-control', 'cursor-pointer', {
                  'is-invalid': isInvalid,
                  'is-dirty': highlightDirty && isDirty,
                })}
                aria-label={uploadButtonText}
              >
                {fileList && fileList.length > 0
                  ? fileList[0].name
                  : emptyText
                    ? emptyText
                    : `No File${multiple ? 's' : ''} Selected`}
              </label>
              <Button
                className={classNames(
                  'btn btn-primary',
                  busy ? 'button-loading' : '',
                )}
                style={{ zIndex: 0 }}
                type="button"
              >
                {uploadButtonText}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  },
);
FileUploadWithForwardedRef.displayName = 'BlobFileUpload';

/**
 * A reusable File Upload control that works with react hook form's register method.  There is a catch, the form data will be a FileList object,
 * use the upload store to handle uploads with FileList objects, you'll have to mutate your Form Data object into the proper entity type for your
 * model before saving it to the back end.
 */
export const FileUpload = observer(FileUploadWithForwardedRef);
